import type { SupportedLocale } from '@erp/shared';
export const batchSelectionTranslations = {
  de: {
    remaining: 'Restlaufzeit am Anlieferdatum: {{days}} Tage',
    customerRequired: 'Bitte zuerst einen Kunden wählen.',
    help: 'Hilfe zur Chargenauswahl',
    retry: 'Erneut laden',
    arrival: 'Geplantes Anlieferdatum',
    arrivalHint: 'Datum der Anlieferung beim Kunden. Ein Versanddatum bestätigt keine Anlieferung.',
    minimum: 'Mindestrestlaufzeit (Kalendertage)',
    priority:
      'Leer: übernehmen. Beleg vor Lieferadresse vor Kunde; strengere Artikelvorgaben gelten weiterhin.',
    warehouse: 'Lagercode',
    allWarehouses: 'Alle Lager',
    available: 'Verfügbar',
    proposed: 'Vorgeschlagen',
    applySplit: 'Aufteilung übernehmen',
    useAlternative: 'Diese Charge verwenden',
    previewOnly:
      'FEFO-Vorschau ohne Reservierung. Bestand und Eignung werden bei der Buchung erneut geprüft. Eine Fehlmenge bleibt als Position ohne Charge erhalten.',
    requirement: 'Benötigt: {{days}} Tage · Herkunft: {{source}}',
    invalidSplit:
      'Die aufzuteilende Position gehört nicht zu diesem Beleg oder verwendet ein anderes Gebinde.',
    missingDocument: 'Für die Chargenprüfung fehlt der Lieferschein.',
    reasons: {
      SUITABLE: 'Geeignet: {{days}} Tage Restlaufzeit, benötigt werden {{required}}.',
      BLOCKED: 'Nicht geeignet: Charge gesperrt oder nicht freigegeben.',
      NO_STOCK: 'Nicht geeignet: kein frei verfügbarer Bestand.',
      WAREHOUSE: 'Nicht geeignet: anderes Lager.',
      MISSING_DATE: 'Nicht bestätigt: geplantes Anlieferdatum fehlt.',
      MISSING_EXPIRY: 'Nicht bestätigt: MHD und Ablaufdatum fehlen.',
      EXPIRED: 'Nicht geeignet: am Anlieferdatum abgelaufen.',
      TOO_SHORT: 'Nicht geeignet: {{days}} Tage Restlaufzeit, benötigt werden {{required}}.',
    },
    sources: {
      DOCUMENT: 'Beleg',
      ADDRESS: 'Lieferadresse (Belegstand)',
      CUSTOMER: 'Kunde',
      PRODUCT: 'Artikel',
      NONE: 'Keine Vorgabe',
    },
    dateKinds: {
      BEST_BEFORE: 'MHD',
      EXPIRY: 'Verbrauchs-/Ablaufdatum',
      NONE: 'Kein Haltbarkeitsdatum',
    },
    errors: {
      TOO_SHORT:
        'Die ausgewählte Charge unterschreitet die Mindestrestlaufzeit am Anlieferdatum. Bitte Chargenauswahl erneut prüfen.',
    },
    statuses: {
      QUARANTINE: 'Quarantäne',
      FREE: 'Frei',
      BLOCKED: 'Gesperrt',
      EXPIRED: 'Abgelaufen',
      DEPLETED: 'Aufgebraucht',
    },
  },
  en: {
    remaining: 'Remaining shelf life on arrival: {{days}} days',
    customerRequired: 'Select a customer first.',
    help: 'Batch selection help',
    retry: 'Reload',
    arrival: 'Planned arrival date',
    arrivalHint: 'Arrival at the customer. A shipping date does not confirm arrival.',
    minimum: 'Minimum remaining shelf life (calendar days)',
    priority:
      'Empty: inherit. Document before delivery address before customer; stricter product requirements still apply.',
    warehouse: 'Warehouse code',
    allWarehouses: 'All warehouses',
    available: 'Available',
    proposed: 'Proposed',
    applySplit: 'Apply split',
    useAlternative: 'Use this batch',
    previewOnly:
      'FEFO preview without reservation. Stock and suitability are checked again at posting. A shortfall remains as a line without a batch.',
    requirement: 'Required: {{days}} days · Source: {{source}}',
    invalidSplit: 'The line to split does not belong to this document or uses a different pack.',
    missingDocument: 'The delivery note required for batch validation is missing.',
    reasons: {
      SUITABLE: 'Suitable: {{days}} days remaining, {{required}} required.',
      BLOCKED: 'Unsuitable: batch blocked or not released.',
      NO_STOCK: 'Unsuitable: no freely available stock.',
      WAREHOUSE: 'Unsuitable: different warehouse.',
      MISSING_DATE: 'Not confirmed: planned arrival date missing.',
      MISSING_EXPIRY: 'Not confirmed: best-before and expiry dates missing.',
      EXPIRED: 'Unsuitable: expired on arrival.',
      TOO_SHORT: 'Unsuitable: {{days}} days remaining, {{required}} required.',
    },
    sources: {
      DOCUMENT: 'Document',
      ADDRESS: 'Delivery address (document snapshot)',
      CUSTOMER: 'Customer',
      PRODUCT: 'Product',
      NONE: 'No requirement',
    },
    dateKinds: {
      BEST_BEFORE: 'Best before',
      EXPIRY: 'Use-by / expiry',
      NONE: 'No shelf-life date',
    },
    errors: {
      TOO_SHORT:
        'The selected batch does not meet the minimum remaining shelf life on arrival. Review the batch selection.',
    },
    statuses: {
      QUARANTINE: 'Quarantine',
      FREE: 'Free',
      BLOCKED: 'Blocked',
      EXPIRED: 'Expired',
      DEPLETED: 'Depleted',
    },
  },
  fr: {
    remaining: 'Conservation restante à l’arrivée : {{days}} jours',
    customerRequired: 'Choisissez d’abord un client.',
    help: 'Aide à la sélection des lots',
    retry: 'Recharger',
    arrival: 'Date d’arrivée prévue',
    arrivalHint: 'Arrivée chez le client. Une date d’expédition ne confirme pas l’arrivée.',
    minimum: 'Durée de conservation minimale restante (jours calendaires)',
    priority:
      'Vide : hériter. Document avant adresse de livraison avant client ; les exigences produit plus strictes restent applicables.',
    warehouse: 'Code d’entrepôt',
    allWarehouses: 'Tous les entrepôts',
    available: 'Disponible',
    proposed: 'Proposé',
    applySplit: 'Appliquer la répartition',
    useAlternative: 'Utiliser ce lot',
    previewOnly:
      'Aperçu FEFO sans réservation. Stock et admissibilité sont revérifiés à la comptabilisation. La quantité manquante reste sur une ligne sans lot.',
    requirement: 'Requis : {{days}} jours · Origine : {{source}}',
    invalidSplit:
      'La ligne à répartir n’appartient pas à ce document ou utilise un autre conditionnement.',
    missingDocument: 'Le bon de livraison nécessaire à la vérification du lot est absent.',
    reasons: {
      SUITABLE: 'Admissible : {{days}} jours restants, {{required}} requis.',
      BLOCKED: 'Non admissible : lot bloqué ou non libéré.',
      NO_STOCK: 'Non admissible : aucun stock disponible.',
      WAREHOUSE: 'Non admissible : autre entrepôt.',
      MISSING_DATE: 'Non confirmé : date d’arrivée prévue absente.',
      MISSING_EXPIRY: 'Non confirmé : dates de durabilité et de péremption absentes.',
      EXPIRED: 'Non admissible : périmé à l’arrivée.',
      TOO_SHORT: 'Non admissible : {{days}} jours restants, {{required}} requis.',
    },
    sources: {
      DOCUMENT: 'Document',
      ADDRESS: 'Adresse de livraison (état du document)',
      CUSTOMER: 'Client',
      PRODUCT: 'Article',
      NONE: 'Aucune exigence',
    },
    dateKinds: {
      BEST_BEFORE: 'Durabilité minimale',
      EXPIRY: 'Date limite de consommation / péremption',
      NONE: 'Aucune date de conservation',
    },
    errors: {
      TOO_SHORT:
        'Le lot choisi ne respecte pas la conservation minimale restante à l’arrivée. Vérifiez la sélection.',
    },
    statuses: {
      QUARANTINE: 'Quarantaine',
      FREE: 'Libre',
      BLOCKED: 'Bloqué',
      EXPIRED: 'Périmé',
      DEPLETED: 'Épuisé',
    },
  },
  es: {
    remaining: 'Vida útil restante a la llegada: {{days}} días',
    customerRequired: 'Seleccione primero un cliente.',
    help: 'Ayuda de selección de lotes',
    retry: 'Recargar',
    arrival: 'Fecha de llegada prevista',
    arrivalHint: 'Llegada al cliente. Una fecha de envío no confirma la llegada.',
    minimum: 'Vida útil restante mínima (días naturales)',
    priority:
      'Vacío: heredar. Documento antes de dirección de entrega antes de cliente; se mantienen los requisitos más estrictos del artículo.',
    warehouse: 'Código de almacén',
    allWarehouses: 'Todos los almacenes',
    available: 'Disponible',
    proposed: 'Propuesto',
    applySplit: 'Aplicar reparto',
    useAlternative: 'Usar este lote',
    previewOnly:
      'Vista previa FEFO sin reserva. Las existencias y la idoneidad se comprueban al contabilizar. La cantidad pendiente queda en una posición sin lote.',
    requirement: 'Se requieren {{days}} días · Origen: {{source}}',
    invalidSplit:
      'La posición que se desea dividir no pertenece a este documento o usa otro envase.',
    missingDocument: 'Falta el albarán necesario para comprobar el lote.',
    reasons: {
      SUITABLE: 'Apto: quedan {{days}} días, se requieren {{required}}.',
      BLOCKED: 'No apto: lote bloqueado o no liberado.',
      NO_STOCK: 'No apto: sin existencias disponibles.',
      WAREHOUSE: 'No apto: otro almacén.',
      MISSING_DATE: 'Sin confirmar: falta la fecha de llegada prevista.',
      MISSING_EXPIRY: 'Sin confirmar: faltan las fechas de consumo preferente y caducidad.',
      EXPIRED: 'No apto: caducado en la fecha de llegada.',
      TOO_SHORT: 'No apto: quedan {{days}} días, se requieren {{required}}.',
    },
    sources: {
      DOCUMENT: 'Documento',
      ADDRESS: 'Dirección de entrega (instantánea del documento)',
      CUSTOMER: 'Cliente',
      PRODUCT: 'Artículo',
      NONE: 'Sin requisito',
    },
    dateKinds: {
      BEST_BEFORE: 'Consumo preferente',
      EXPIRY: 'Caducidad',
      NONE: 'Sin fecha de conservación',
    },
    errors: {
      TOO_SHORT:
        'El lote elegido no cumple la vida útil mínima restante a la llegada. Revise la selección.',
    },
    statuses: {
      QUARANTINE: 'Cuarentena',
      FREE: 'Libre',
      BLOCKED: 'Bloqueado',
      EXPIRED: 'Caducado',
      DEPLETED: 'Agotado',
    },
  },
  tr: {
    remaining: 'Varış tarihinde kalan raf ömrü: {{days}} gün',
    customerRequired: 'Önce bir müşteri seçin.',
    help: 'Parti seçimi yardımı',
    retry: 'Yeniden yükle',
    arrival: 'Planlanan varış tarihi',
    arrivalHint: 'Müşteriye varış tarihi. Sevk tarihi, varışı doğrulamaz.',
    minimum: 'Asgari kalan raf ömrü (takvim günü)',
    priority:
      'Boş: devral. Belge, teslimat adresi, müşteri sırasıyla uygulanır; daha sıkı ürün koşulları geçerliliğini korur.',
    warehouse: 'Depo kodu',
    allWarehouses: 'Tüm depolar',
    available: 'Kullanılabilir',
    proposed: 'Önerilen',
    applySplit: 'Dağılımı uygula',
    useAlternative: 'Bu partiyi kullan',
    previewOnly:
      'Rezervasyon yapmayan FEFO önizlemesi. Stok ve uygunluk kayıt sırasında tekrar denetlenir. Eksik miktar partisiz satır olarak kalır.',
    requirement: 'Gereken: {{days}} gün · Kaynak: {{source}}',
    invalidSplit: 'Bölünecek satır bu belgeye ait değil veya farklı ambalaj kullanıyor.',
    missingDocument: 'Parti kontrolü için gereken irsaliye eksik.',
    reasons: {
      SUITABLE: 'Uygun: {{days}} gün raf ömrü kaldı, {{required}} gün gerekli.',
      BLOCKED: 'Uygun değil: parti bloke edilmiş veya serbest bırakılmamış.',
      NO_STOCK: 'Uygun değil: serbest stok yok.',
      WAREHOUSE: 'Uygun değil: farklı depo.',
      MISSING_DATE: 'Doğrulanmadı: planlanan varış tarihi eksik.',
      MISSING_EXPIRY: 'Doğrulanmadı: tavsiye edilen tüketim ve son kullanma tarihleri eksik.',
      EXPIRED: 'Uygun değil: varış tarihinde süresi dolmuş.',
      TOO_SHORT: 'Uygun değil: {{days}} gün raf ömrü kaldı, {{required}} gün gerekli.',
    },
    sources: {
      DOCUMENT: 'Belge',
      ADDRESS: 'Teslimat adresi (belge kaydı)',
      CUSTOMER: 'Müşteri',
      PRODUCT: 'Ürün',
      NONE: 'Koşul yok',
    },
    dateKinds: {
      BEST_BEFORE: 'Tavsiye edilen tüketim',
      EXPIRY: 'Son kullanma tarihi',
      NONE: 'Raf ömrü tarihi yok',
    },
    errors: {
      TOO_SHORT:
        'Seçilen parti varış tarihinde asgari kalan raf ömrünü karşılamıyor. Parti seçimini tekrar kontrol edin.',
    },
    statuses: {
      QUARANTINE: 'Karantina',
      FREE: 'Serbest',
      BLOCKED: 'Bloke',
      EXPIRED: 'Süresi dolmuş',
      DEPLETED: 'Tükenmiş',
    },
  },
} as const satisfies Record<SupportedLocale, object>;
